import { ChatInitContentItem, ChatInitResponse, ResolvedChatbotInit } from '../types';

const EMPTY_LABELS: ResolvedChatbotInit['labels'] = {
  openChat: '',
  openChatWithNameTemplate: '',
  minimizeChat: '',
  endChat: '',
  cancelRequest: '',
  thinking: '',
  errorTitle: '',
  errorFallbackMessage: '',
  messageFailedTitle: '',
  messageFailedBody: '',
  returnToChat: '',
  unableToConnectTitle: '',
  unableToConnectBody: '',
  close: '',
  dismiss: '',
  retry: '',
  userLabel: '',
  assistantFallbackName: '',
  inputPlaceholder: '',
  sendMessageAria: '',
  maxCharsHintTemplate: '',
  charLimitExceededSuffix: '',
  imageAlt: '',
  endChatConfirmTitle: '',
  endChatConfirmBody: '',
  endChatConfirmReturn: '',
  endChatConfirmEnd: '',
  surveyTitle: '',
  surveyDisclaimer: '',
  surveySkip: '',
  surveySubmit: '',
  feedbackThankYouMessage: '',
  feedbackInputTitle: '',
  thumbsUp: '',
  thumbsDown: ''
};

const toNonEmptyString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

interface LabelMaps {
  displayMap: Record<string, string>;
  tooltipMap: Record<string, string>;
}

const createLabelMaps = (contents: ChatInitContentItem[] = []): LabelMaps => {
  return contents.reduce<LabelMaps>((accumulator, item) => {
    if (!item?.keyName) {
      return accumulator;
    }

    const display = toNonEmptyString(item.display);
    const textToRead = toNonEmptyString(item.textToRead);
    const resolvedLabel = display || textToRead;

    if (resolvedLabel) {
      accumulator.displayMap[item.keyName] = resolvedLabel;
      accumulator.tooltipMap[item.keyName] = textToRead || resolvedLabel;
    }

    return accumulator;
  }, { displayMap: {}, tooltipMap: {} });
};

export const resolveChatbotInit = (initData?: ChatInitResponse | null): ResolvedChatbotInit => {
  const contents = initData?.contentResource?.contents || [];
  const { displayMap: labelMap, tooltipMap } = createLabelMaps(contents);

  const labels: ResolvedChatbotInit['labels'] = {
    ...EMPTY_LABELS,
    openChat: labelMap.btnOpenChat || '',
    openChatWithNameTemplate: labelMap.btnOpenChatWith || '',
    minimizeChat: labelMap.btnMinimizeChat || '',
    endChat: labelMap.btnEndChat || '',
    cancelRequest: labelMap.btnClose || '',
    thinking: labelMap.lblThinking || '',
    errorTitle: labelMap.lblSomethingWentWrong || '',
    errorFallbackMessage: labelMap.lblIssueRetry || '',
    messageFailedTitle: labelMap.lblMessageFailedToSend || '',
    messageFailedBody: labelMap.lblMessageNotSent || labelMap.lblMessageRetryBody || '',
    returnToChat: labelMap.btnReturnToChat || '',
    unableToConnectTitle: labelMap.lblUnableToConnect || labelMap.lblUnableToConnectTitle || '',
    unableToConnectBody: labelMap.lblConnectionIssue || labelMap.lblUnableToConnectBody || '',
    close: labelMap.btnClose || '',
    dismiss: labelMap.btnDismiss || '',
    retry: labelMap.btnRetry || '',
    userLabel: labelMap.lblYou || '',
    assistantFallbackName: labelMap.lblTitle || '',
    inputPlaceholder: labelMap.phTypeMessage || '',
    sendMessageAria: labelMap.btnSendMessage || '',
    maxCharsHintTemplate: labelMap.lblMaxCharacters || '',
    charLimitExceededSuffix: labelMap.lblCharLimitExceeded || '',
    imageAlt: labelMap.lblChatbotImageAlt || '',
    endChatConfirmTitle: labelMap.lblEndChatConfirmTitle || labelMap.btnEndChat || '',
    endChatConfirmBody: labelMap.lblEndChatConfirmation || '',
    endChatConfirmReturn: labelMap.btnReturnToChat || '',
    endChatConfirmEnd: labelMap.btnEndChatConfirm || labelMap.btnEndChat || '',
    surveyTitle: labelMap.lblFeedbackHeading || labelMap.lblSurveyTitle || '',
    surveyDisclaimer: labelMap.lblFeedbackDisclaimer || labelMap.lblSurveyDisclaimer || '',
    surveySkip: labelMap.btnSkip || labelMap.btnSurveySkip || '',
    surveySubmit: labelMap.btnSubmit || labelMap.btnSurveySubmit || '',
    feedbackThankYouMessage: labelMap.msgFeedbackThankYou || '',
    feedbackInputTitle: labelMap.lblFeedbackInputTitle || '',
    thumbsUp: labelMap.lblThumbsUp || '',
    thumbsDown: labelMap.lblThumbsDown || '',
  };

  const tooltips: ResolvedChatbotInit['tooltips'] = {
    ...EMPTY_LABELS,
    openChat: tooltipMap.btnOpenChat || '',
    openChatWithNameTemplate: tooltipMap.btnOpenChatWith || '',
    minimizeChat: tooltipMap.btnMinimizeChat || '',
    endChat: tooltipMap.btnEndChat || '',
    cancelRequest: tooltipMap.btnClose || '',
    thinking: tooltipMap.lblThinking || '',
    errorTitle: tooltipMap.lblSomethingWentWrong || '',
    errorFallbackMessage: tooltipMap.lblIssueRetry || '',
    messageFailedTitle: tooltipMap.lblMessageFailedToSend || '',
    messageFailedBody: tooltipMap.lblMessageNotSent || tooltipMap.lblMessageRetryBody || '',
    returnToChat: tooltipMap.btnReturnToChat || '',
    unableToConnectTitle: tooltipMap.lblUnableToConnect || tooltipMap.lblUnableToConnectTitle || '',
    unableToConnectBody: tooltipMap.lblConnectionIssue || tooltipMap.lblUnableToConnectBody || '',
    close: tooltipMap.btnClose || '',
    dismiss: tooltipMap.btnDismiss || '',
    retry: tooltipMap.btnRetry || '',
    userLabel: tooltipMap.lblYou || '',
    assistantFallbackName: tooltipMap.lblTitle || '',
    inputPlaceholder: tooltipMap.phTypeMessage || '',
    sendMessageAria: tooltipMap.btnSendMessage || '',
    maxCharsHintTemplate: tooltipMap.lblMaxCharacters || '',
    charLimitExceededSuffix: tooltipMap.lblCharLimitExceeded || '',
    imageAlt: tooltipMap.lblChatbotImageAlt || '',
    endChatConfirmTitle: tooltipMap.lblEndChatConfirmTitle || tooltipMap.btnEndChat || '',
    endChatConfirmBody: tooltipMap.lblEndChatConfirmation || '',
    endChatConfirmReturn: tooltipMap.btnReturnToChat || '',
    endChatConfirmEnd: tooltipMap.btnEndChatConfirm || tooltipMap.btnEndChat || '',
    surveyTitle: tooltipMap.lblFeedbackHeading || tooltipMap.lblSurveyTitle || '',
    surveyDisclaimer: tooltipMap.lblFeedbackDisclaimer || tooltipMap.lblSurveyDisclaimer || '',
    surveySkip: tooltipMap.btnSkip || tooltipMap.btnSurveySkip || '',
    surveySubmit: tooltipMap.btnSubmit || tooltipMap.btnSurveySubmit || '',
    feedbackThankYouMessage: tooltipMap.msgFeedbackThankYou || '',
    feedbackInputTitle: tooltipMap.lblFeedbackInputTitle || '',
    thumbsUp: tooltipMap.lblThumbsUp || '',
    thumbsDown: tooltipMap.lblThumbsDown || '',
  };

  const resolvedAssistantName = toNonEmptyString(initData?.assistantName) || labels.assistantFallbackName;

  return {
    isEnabled: typeof initData?.isChatEnabled === 'boolean' ? initData.isChatEnabled : true,
    assistantName: resolvedAssistantName || labels.assistantFallbackName,
    chatWindowTitle : labelMap.lblChatWindowTitle || '',
    greetingHtml: toNonEmptyString(initData?.greetings),
    disclaimerHtml: toNonEmptyString(initData?.disclaimer),
    labels,
    tooltips,
  };
};
